// ═══════════════════════════════════════════════════════════════
// SPONSORSHIP CARD
// Path: src/components/shows/SponsorshipCard.jsx
//
// Collapsible card for one sponsorship. Shows credit_as_printed,
// show + package name, gift amount (canViewGifts only), progress bar.
// Expands to BenefitFulfillmentPanel.
//
// Props:
//   sponsorship     — sponsorship row with nested shows, packages, fulfillments
//   canEdit         — Admin/Manager can mark fulfillment
//   canDelete       — Admin only
//   canViewGifts    — show dollar amounts
//   chapterId       — for custom benefit creation
//   userId          — current user
//   onUpdate        — () => void — callback after changes
//   onDelete        — (sponsorshipId) => void
//   showMessage     — (text, type) => void
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 5
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  Film,
  Package,
  DollarSign,
  Trash2,
  User,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { FulfillmentProgressBar } from "./FulfillmentProgressBar";
import { BenefitFulfillmentPanel } from "./BenefitFulfillmentPanel";
import { fulfillmentsService } from "../../services/showSponsorshipsService";

const formatCurrency = (amount) => {
  if (amount == null) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const SponsorshipCard = ({
  sponsorship,
  canEdit = false,
  canDelete = false,
  canViewGifts = false,
  chapterId,
  userId,
  onUpdate,
  onDelete,
  showMessage,
}) => {
  const { palette } = useApp();
  const [expanded, setExpanded] = useState(false);
  const [fulfillments, setFulfillments] = useState(
    sponsorship.sponsorship_benefit_fulfillments || []
  );

  const showInfo = sponsorship.shows;
  const pkgInfo = sponsorship.sponsorship_packages;
  const memberName = sponsorship.memberName;

  const fulfilledCount = fulfillments.filter((f) => f.fulfilled_at).length;
  const totalBenefits = fulfillments.length;

  // ── Load/refresh fulfillments ─────────────────────────────
  const loadFulfillments = useCallback(async () => {
    try {
      const data = await fulfillmentsService.getFulfillments(sponsorship.id);
      setFulfillments(data);
    } catch (error) {
      // silent — will show stale data
    }
  }, [sponsorship.id]);

  const handleUpdate = () => {
    loadFulfillments();
    onUpdate?.();
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <Card style={{ padding: 14, marginBottom: 8 }}>
      {/* Header — always visible */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Credit name */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 700,
                color: palette.text1,
                fontFamily: FONT_FAMILY,
              }}
            >
              {sponsorship.credit_as_printed}
            </span>

            {/* Member name if different from credit */}
            {memberName &&
              memberName !== sponsorship.credit_as_printed && (
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <User size={ICON_SIZES.xs} />
                  {memberName}
                </span>
              )}
          </div>

          {/* Show + Package + Amount */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              fontSize: FONT_SIZES.xs,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            {showInfo && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Film size={ICON_SIZES.xs} />
                {showInfo.title} ({showInfo.season})
              </span>
            )}
            {pkgInfo && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Package size={ICON_SIZES.xs} />
                {pkgInfo.name}
              </span>
            )}
            {canViewGifts && pkgInfo?.min_amount && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  color: palette.accent1,
                  fontWeight: 600,
                }}
              >
                <DollarSign size={ICON_SIZES.xs} />
                {formatCurrency(pkgInfo.min_amount)}+
              </span>
            )}
          </div>
        </div>

        {/* Progress bar + chevron */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
            minWidth: 180,
          }}
        >
          <FulfillmentProgressBar
            fulfilled={fulfilledCount}
            total={totalBenefits}
          />
          {expanded ? (
            <ChevronUp size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          ) : (
            <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          )}
        </div>
      </div>

      {/* Expanded — fulfillment checklist */}
      {expanded && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${palette.cardBorder}`,
          }}
        >
          <BenefitFulfillmentPanel
            sponsorshipId={sponsorship.id}
            fulfillments={fulfillments}
            canEdit={canEdit}
            canAddCustom={canEdit}
            chapterId={chapterId}
            userId={userId}
            onUpdate={handleUpdate}
            showMessage={showMessage}
          />

          {/* Notes */}
          {sponsorship.notes && (
            <p
              style={{
                margin: "10px 0 0",
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
                fontStyle: "italic",
              }}
            >
              {sponsorship.notes}
            </p>
          )}

          {/* Delete (Admin only) */}
          {canDelete && (
            <div style={{ marginTop: 10, textAlign: "right" }}>
              <ActionButton
                label="Delete Sponsorship"
                icon={Trash2}
                variant="danger"
                small
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(sponsorship.id);
                }}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
